import React, { useRef } from "react";
import { Helmet } from "react-helmet";

import Header from "../templates/header";
import Footer from "../templates/footer";
import ThreeStepPlan from "../components/three-step-plan";
import Benefits from "../components/benefits";
import CallToAction from "../components/call-to-action";
import ReviewsCarousel from "../components/reviews-carousel";
import StayInTouch from "../components/stay-in-touch";

import "../buttons.css";
import "./index.css";
import "./video-player.css";

import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";

import Modal from "react-modal";
import ReactPlayer from "react-player/lazy";
// import Drift from "react-driftjs"; // Currently unused

import { graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

// markup
const IndexPage = ({ data }) => {
  const ctaRef = useRef(null);
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [playing, setPlaying] = React.useState(false);

  // https://stackoverflow.com/questions/52879362/gatsby-open-modal-window-on-button-click
  Modal.setAppElement("#___gatsby");

  function watchVideo() {
    setIsOpen(true);
  }

  function afterOpenModal() {
    setPlaying(true);
  }

  function closeModal() {
    setPlaying(false);
    setIsOpen(false);
  }

  function executeScroll() {
    ctaRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // Laundry Day, Done. Sit back and relax. Your clean laundry delivered in 2 days, guaranteed.
  return (
    <div>
      <Helmet>
        <title>Cloto Laundry Service Toronto | Pickup & Delivery | 2-Day Guarantee</title>
        <meta name="description" content="Toronto's premium laundry pickup and delivery service. Wash, fold, and dry cleaning with guaranteed 2-day turnaround. Serving Scarborough, Markham, Pickering & GTA."/>
      </Helmet>
      <Header></Header>
      <Modal
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={closeModal}
        shouldCloseOnEsc={true}
        contentLabel="Laundry Day Blues?"
        className="video-modal"
        overlayClassName="video-modal-overlay"
      >
        <ReactPlayer
          url="https://vimeo.com/503762662"
          width="100%"
          height="100%"
          playing={playing}
        />
      </Modal>
      <main>
        <div className="relative bg-white overflow-hidden">
          {/* Background image - absolute on all screens */}
          <div className="absolute inset-0 lg:left-1/2 lg:right-0">
            <GatsbyImage
              image={getImage(data.unhampered)}
              className="h-full w-full"
              imgStyle={{ objectFit: 'cover', objectPosition: '70% center' }}
              alt="Life unhampered with Cloto laundry service"
            />
          </div>
          <div className="relative max-w-7xl mx-auto min-h-[400px] sm:min-h-[450px] md:min-h-[500px] lg:min-h-0">
            <div className="relative z-10 pb-8 lg:pb-16 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32 lg:bg-white">
              <svg
                className="hidden lg:block absolute right-0 inset-y-0 h-full w-48 text-white transform translate-x-1/2"
                fill="currentColor"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <polygon points="50,0 100,0 50,100 0,100" />
              </svg>

              <div className="relative pt-6 px-4 sm:px-6 lg:px-8"></div>

              <div className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                <div className="lg:text-left">
                  <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                    <span className="block">Life</span>
                    <span className="block clogreen">Unhampered</span>
                  </h1>
                  <p className="mt-3 text-base text-gray-700 sm:mt-5 sm:text-lg sm:max-w-xl md:mt-5 md:text-xl lg:mx-0 lg:text-gray-500">
                    You've Got Better Things To Do Than Laundry
                  </p>
                  <div className="mt-5 sm:mt-8 sm:flex lg:justify-start">
                    <div>
                      <button
                        className="cursor-pointer w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md clobutton md:py-4 md:text-lg md:px-10"
                        onClick={(e) => watchVideo()}
                      >
                        Watch the Video
                      </button>
                    </div>
                    <div className="mt-3 sm:mt-0 sm:ml-3">
                      <button
                        className="cursor-pointer w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 md:py-4 md:text-lg md:px-10"
                        onClick={(e) => executeScroll()}
                      >
                        Get Started
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ThreeStepPlan data={data} />
        <div class="mx-auto px-4 sm:px-6 lg:px-8 bg-indigo-50 pt-8">
          <div class="max-w-3xl mx-auto">
            <h2 className="font-extrabold tracking-tight text-gray-900 text-3xl md:text-4xl mb-6 text-center">
              Why Choose Cloto for Toronto Laundry Service?
            </h2>
            <p className="leading-relaxed">
              Do you dream of not having to lug hampers up and down the stairs?
              Or not having to spend hours washing and drying load after load?
              Do you dream of the laundry folding itself instead of piling up on
              the bed? Do you wish your comforters could fit in your home
              washer? Wish you didn’t have to lug your stuff to the laundromat?
              Would you like an easy way to reduce waste and go green? If you
              answered ‘yes’ to any of the questions above, then you know why
              Cloto is perfect for you.{" "}
              <strong>
                Reclaim your time for the more important things in life – a life
                unhampered with Cloto's Toronto area laundry pickup and delivery service.
              </strong>
            </p>
            <div className="mt-6">
              <h3 className="text-xl font-semibold mb-3">Serving the Greater Toronto Area</h3>
              <p className="text-gray-700">
                Cloto provides professional laundry pickup and delivery service throughout{" "}
                <strong>
                  Toronto, Scarborough, Markham, Pickering, Ajax, Whitby, Oshawa, Brooklin,
                  Bowmanville, and surrounding GTA communities.
                </strong>{" "}
                Our wash and fold service includes free pickup and delivery with a guaranteed
                2-day turnaround time, so your home laundry to-do list practically manages itself.
                We also provide commercial laundry solutions for businesses needing laundry services.
                Whether you need bulk linen service for your massage clinic, bulk linens for a healthcare office,
                or need bulk towel laundry and fresh bulk towels for your salon, spa, or barbershop,
                we've got you covered with dependable commercial laundry pickup and delivery.
              </p>
            </div>
          </div>
        </div>
        <div className="mx-auto p-6 sm:p-6 lg:px-8 bg-indigo-50"></div>
        <Benefits data={data} />
        <div ref={ctaRef}>
          <CallToAction data={data} />
        </div>
        <ReviewsCarousel data={data} />
        <StayInTouch />
      </main>
      <Footer></Footer>
    </div>
  );
};

export const query = graphql`query ImageQuery {
  schedule: file(relativePath: {eq: "schedule.png"}) {
    childImageSharp {
      gatsbyImageData(width: 240, height: 240, layout: CONSTRAINED)
    }
  }
  relax: file(relativePath: {eq: "relax.png"}) {
    childImageSharp {
      gatsbyImageData(width: 240, height: 240, layout: CONSTRAINED)
    }
  }
  download: file(relativePath: {eq: "download.png"}) {
    childImageSharp {
      gatsbyImageData(width: 240, height: 240, layout: CONSTRAINED)
    }
  }
  unhampered: file(relativePath: {eq: "laundry-unhampered.jpg"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
  environment: file(relativePath: {eq: "environment.jpg"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
  familyTime: file(relativePath: {eq: "family-time.jpg"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
  twoTotes: file(relativePath: {eq: "two-totes.png"}) {
    childImageSharp {
      gatsbyImageData(width: 303, height: 157, layout: CONSTRAINED)
    }
  }
  threeTotes: file(relativePath: {eq: "three-totes.png"}) {
    childImageSharp {
      gatsbyImageData(width: 303, height: 157, layout: CONSTRAINED)
    }
  }
}`;

export default IndexPage;
